// Apolo IDE — renderer-side consumer for the unified agent-turn SSE (E4-T02).
//
// The Worker's streaming /agent-turn re-emits a UNIFIED Apolo SSE regardless of
// provider (Anthropic/OpenAI-compat — normalized server-side):
//   event: delta\ndata: {"type":"token"|"reasoning","text":"..."}   per token
//   event: done\ndata: {content, tool_calls, usage}                 final reply
//   event: error\ndata: {error}                                     → fall back
//
// consumeAgentStream reads that stream from a ReadableStreamDefaultReader, calls
// `onDelta({ type, delta })` for each token/reasoning frame (the SHAPE the
// agentLoop's 2nd argument expects — it reads delta.type + delta.delta), and
// resolves with the assembled reply `{ ok:true, content, tool_calls, usage }`.
//
// SAFETY: it NEVER throws. On any problem — an `error` frame, a stream that ends
// without a `done` frame, a null reader, or a mid-stream read failure — it
// resolves `{ ok:false, fallback:true }`. The caller treats that as "use the
// proven non-streaming endpoint", so a streaming bug can never break a cloud turn.

const FALLBACK = (error, started = false) => ({ ok: false, fallback: true, error, started });

/**
 * @param {ReadableStreamDefaultReader|null} reader  from res.body.getReader()
 * @param {(d:{type:'token'|'reasoning', delta:string})=>void} onDelta
 * @returns {Promise<{ok:boolean, content?:string, tool_calls?:any[], usage?:object, finish_reason?:string|null, fallback?:boolean, error?:string, started?:boolean}>}
 *   `started` is true once ANY token/reasoning frame was emitted — the upstream
 *   call is in flight and (for billed providers) already charged, so the caller
 *   must NOT re-run it (E2-WS1-T01 no-double-charge guard).
 */
export async function consumeAgentStream(reader, onDelta) {
  if (!reader || typeof reader.read !== 'function') return FALLBACK('no stream reader');

  const decoder = new TextDecoder();
  let buffer = '';
  let curEvent = 'message';
  let curData = '';
  let done = null; // the parsed `done` payload, once seen
  let sawError = null;
  let tokensEmitted = 0; // E2-WS1-T01 — did the stream actually start producing?
  let assembledContent = ''; // best-effort content from token frames (no `done`)

  // Process one complete SSE frame (separated by a blank line).
  const flushFrame = () => {
    const event = curEvent;
    const data = curData;
    curEvent = 'message';
    curData = '';
    if (!data) return;
    if (event === 'delta') {
      let parsed;
      try { parsed = JSON.parse(data); } catch { return; } // skip malformed delta
      if (parsed && (parsed.type === 'token' || parsed.type === 'reasoning') && parsed.text) {
        tokensEmitted += 1;
        if (parsed.type === 'token') assembledContent += parsed.text;
        try { onDelta?.({ type: parsed.type, delta: parsed.text }); } catch { /* UI cb never breaks the stream */ }
      } else if (parsed && parsed.type === 'tool_activity') {
        // Auditoria 2026-07-02 — sinal do Worker de que o upstream começou a
        // produzir tool calls (turno só-tool-call não emite token/reasoning).
        // Conta como "started" para o no-double-charge guard, mas NÃO é texto:
        // não vira assembledContent nem onDelta.
        tokensEmitted += 1;
      }
    } else if (event === 'done') {
      try { done = JSON.parse(data); } catch { done = null; }
    } else if (event === 'error') {
      try { sawError = JSON.parse(data)?.error || 'stream error'; } catch { sawError = 'stream error'; }
    }
  };

  // Feed accumulated text, emitting frames on blank-line boundaries.
  const pump = (text) => {
    buffer += text;
    let nl;
    while ((nl = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nl).replace(/\r$/, '');
      buffer = buffer.slice(nl + 1);
      if (line === '') { flushFrame(); continue; }
      if (line.startsWith('event:')) curEvent = line.slice(6).trim();
      else if (line.startsWith('data:')) curData += line.slice(5).trim();
      // other field lines (id:, retry:, comments) are ignored
    }
  };

  try {
    for (;;) {
      const { value, done: streamDone } = await reader.read();
      if (streamDone) break;
      pump(decoder.decode(value, { stream: true }));
    }
    // Final flush — any buffered partial frame at EOF.
    if (buffer.length) pump('\n\n');
    else flushFrame();
  } catch (e) {
    return FALLBACK(e?.message || 'stream read failed', tokensEmitted > 0);
  }

  // Either failure mode carries the best-effort partial content + `started` so a
  // started-then-failed stream is returned (not re-run) by runCloudTurn.
  if (sawError) return { ...FALLBACK(sawError, tokensEmitted > 0), content: assembledContent };
  // A stream that never delivered a `done` frame is untrustworthy as a final
  // answer — fall back so we never hand the loop a truncated reply. `started`
  // carries whether tokens were already emitted (billed) so the caller can
  // avoid re-running a started-then-failed stream.
  if (!done || typeof done !== 'object') {
    return { ...FALLBACK('stream ended without done frame', tokensEmitted > 0), content: assembledContent };
  }

  const doneFrame = /** @type {any} */ (done);
  return {
    ok: true,
    content: typeof doneFrame.content === 'string' ? doneFrame.content : '',
    tool_calls: Array.isArray(doneFrame.tool_calls) ? doneFrame.tool_calls : [],
    usage: doneFrame.usage || undefined,
    // Wave-B — despacho de truncamento/filtro no agentLoop (classifyFinish).
    finish_reason: typeof doneFrame.finish_reason === 'string' ? doneFrame.finish_reason : null,
  };
}

/** A user-initiated abort (Stop) — re-thrown by runCloudTurn so the loop stops. */
export function isUserAbort(signal, err) {
  if (signal?.aborted) return true;
  return err?.name === 'AbortError';
}

/**
 * Run ONE cloud agent turn with live streaming + a HARD fallback to the proven
 * non-streaming path. This is the brain of the cloud chatFn, factored out so the
 * fallback decision is unit-testable.
 *
 * Order of operations:
 *   1. Try `streamFetch(signal)` → an SSE Response. If it's OK and has a body,
 *      consume it with onDelta. A successful `done` frame → return that reply.
 *   2. On ANY streaming problem — fetch throws (not a user abort), non-OK status,
 *      no body, an `error` frame, or a stream without `done` — fall through to
 *      `nonStreamCall(signal)` (the existing /agent-turn JSON call). That call's
 *      result is returned verbatim, so a streaming bug NEVER breaks a cloud turn.
 *   3. A user Stop (signal aborted / AbortError) is re-thrown — the loop must
 *      stop, not silently fall back and keep spending.
 *
 * E2-WS1-T01 NO-DOUBLE-CHARGE GUARD: a stream that ALREADY emitted tokens before
 * failing has an in-flight (billed) upstream call. Re-running the non-streaming
 * endpoint would issue a SECOND upstream call and double-charge. So we only fall
 * back to `nonStreamCall` when the stream produced ZERO tokens (never started);
 * a started-then-failed stream returns its best-effort partial instead.
 *
 * @param {object} opts
 * @param {(signal:AbortSignal)=>Promise<Response>} opts.streamFetch
 * @param {(signal:AbortSignal)=>Promise<object>}   opts.nonStreamCall  proven path
 * @param {(d:object)=>void} opts.onDelta
 * @param {AbortSignal=} opts.signal
 */
export async function runCloudTurn({ streamFetch, nonStreamCall, onDelta, signal }) {
  // Attempt streaming first.
  try {
    const res = await streamFetch(signal);
    if (res && res.ok && res.body && typeof res.body.getReader === 'function') {
      const streamed = await consumeAgentStream(res.body.getReader(), onDelta);
      if (streamed.ok) {
        return {
          ok: true,
          content: streamed.content,
          tool_calls: streamed.tool_calls,
          usage: streamed.usage,
          finish_reason: streamed.finish_reason ?? null,
        };
      }
      // E2-WS1-T01 — the stream STARTED (emitted tokens) then failed: the
      // upstream call is already in flight/billed. Do NOT re-run it. Return the
      // best-effort partial as a terminal reply (no tool_calls → the loop ends
      // this turn cleanly) rather than issuing a second, double-charging call.
      // FASE-11 E2-WS3-T02 — o parcial NÃO volta mais disfarçado de resposta
      // completa: emite um delta 'stream_truncated' (o loop repassa ao onEvent →
      // mensagem visível no chat) e marca o reply com truncated/error, para
      // telemetria e chamadores que queiram reagir. Era 100% silencioso (#8/#24).
      if (streamed.started) {
        try { onDelta?.({ type: 'stream_truncated', error: streamed.error || 'stream interrompido' }); } catch { /* surface é best-effort */ }
        return {
          ok: true,
          truncated: true,
          error: streamed.error || 'stream interrompido',
          content: typeof streamed.content === 'string' ? streamed.content : '',
          tool_calls: [],
          usage: streamed.usage,
        };
      }
      // streamed.fallback with ZERO tokens → safe to fall through (never billed).
    }
    // non-OK / no body → fall through (no upstream tokens produced).
  } catch (e) {
    if (isUserAbort(signal, e)) throw e; // Stop wins — do not silently fall back.
    // A fetch that threw before any token never charged → safe to fall through.
  }

  // HARD FALLBACK — the proven, fully-tested non-streaming endpoint. Reached only
  // when no upstream tokens were produced, so it issues exactly one billed call.
  return nonStreamCall(signal);
}
